import { type StockStatus } from '@choco-admin/types';

/** Every status, in the order a payload lists them. */
const STOCK_STATUSES: readonly StockStatus[] = [
  'InStock',
  'OutOfStock',
  'Discontinued',
];

/** Narrows a wire value, which codegen widens with `'%future added value'`. */
const isStockStatus = (value: string): value is StockStatus =>
  (STOCK_STATUSES as readonly string[]).includes(value);

/**
 * The stored statuses, keeping only the ones this portal knows about.
 * Absent or empty stays null: the backend reads both as in-stock only.
 */
export const toBuyerVisibleStockStatuses = (
  values: readonly string[] | null | undefined
): StockStatus[] | null => {
  const known = (values ?? []).filter(isStockStatus);
  return known.length > 0 ? known : null;
};

/** Whether buyers see out-of-stock products. */
export const showsOutOfStockProducts = (
  stored: readonly StockStatus[] | null | undefined
): boolean => stored?.includes('OutOfStock') ?? false;

/**
 * The statuses to store once out-of-stock visibility is set to `show`.
 *
 * Only `OutOfStock` is toggled; any other stored status is kept. `InStock` is
 * always included, because the backend rejects a list without it.
 */
export const withOutOfStockVisibility = (
  stored: readonly StockStatus[] | null | undefined,
  show: boolean
): StockStatus[] =>
  STOCK_STATUSES.filter((status) => {
    if (status === 'InStock') return true;
    if (status === 'OutOfStock') return show;
    return stored?.includes(status) ?? false;
  });
